import { Col } from "@amzn/stencil-react-components/layout";
import React, { ComponentProps, FC, ReactNode } from "react";
import { NEUTRAL_BORDER_COLOR } from "@/common/styles/design-tokens.constants";

/**
 * Border radius steps this card supports, as design token variables.
 *
 * Stencil's `borderRadius` prop only understands its legacy `R*` keys and passes
 * anything else through verbatim, so a bare token step like "200" would emit an
 * invalid unitless `border-radius: 200` and the corners would render square. The
 * 12px step has no `R*` key at all, so all steps go through the CSS variables.
 */
export const RADIUS_TOKENS = {
  /** 8px — form detail panels and review cards. */
  "200": "var(--st-dt-dimensions-border-radius-200)",
  /** 12px — order summary and order progress cards. */
  "300": "var(--st-dt-dimensions-border-radius-300)",
} as const;

interface BorderedCardProps {
  children: ReactNode;
  borderRadius?: keyof typeof RADIUS_TOKENS;
  padding?: ComponentProps<typeof Col>["padding"];
  gridGap?: string;
  dataTestId?: string;
}

/**
 * Neutral outlined surface used throughout the laptop lifecycle replacement
 * screens for form detail panels, summary cards and order panels.
 */
export const BorderedCard: FC<BorderedCardProps> = ({
  children,
  borderRadius = "200",
  padding = "S300",
  gridGap = "S200",
  dataTestId,
}) => (
  <Col
    width="100%"
    backgroundColor="neutral0"
    border={`1px solid ${NEUTRAL_BORDER_COLOR}`}
    borderRadius={RADIUS_TOKENS[borderRadius]}
    padding={padding}
    gridGap={gridGap}
    data-testid={dataTestId}
  >
    {children}
  </Col>
);
